/**
 * Browser Notifications Service
 * Handles browser Notification API integration for real-time alerts
 */

import { createStore, Store } from './store';
import { Router } from './router';

/**
 * Browser notification permission states
 */
export type NotificationPermission = 'default' | 'granted' | 'denied';

/**
 * Notification event types for expenditure requests
 */
export type NotificationEventType =
  | 'pending_approval'
  | 'request_approved'
  | 'request_rejected'
  | 'comment_added'
  | 'request_submitted'
  | 'request_cancelled';

/**
 * Browser notification options
 */
export interface BrowserNotificationOptions {
  /** Notification title */
  title: string;
  /** Notification body text */
  body: string;
  /** Notification icon URL */
  icon?: string;
  /** Notification badge icon URL */
  badge?: string;
  /** Notification tag (for grouping/replacing) */
  tag?: string;
  /** Data to pass when notification is clicked */
  data?: BrowserNotificationData;
  /** Whether to require interaction before closing */
  requireInteraction?: boolean;
  /** Vibration pattern (mobile) */
  vibrate?: number[];
  /** Auto-close timeout in ms (0 = no auto-close) */
  autoClose?: number;
}

/**
 * Data attached to browser notifications for click handling
 */
export interface BrowserNotificationData {
  /** Type of notification event */
  type: NotificationEventType;
  /** Related request ID */
  requestId?: string;
  /** Related request title */
  requestTitle?: string;
  /** URL to navigate to on click */
  url?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Browser notifications state
 */
export interface BrowserNotificationsState {
  /** Current permission status */
  permission: NotificationPermission;
  /** Whether notifications are enabled by user preference */
  enabled: boolean;
  /** Whether the browser supports notifications */
  isSupported: boolean;
  /** Specific notification type preferences */
  preferences: NotificationPreferences;
}

/**
 * User preferences for specific notification types
 */
export interface NotificationPreferences {
  /** Notify when there's a pending approval */
  pendingApproval: boolean;
  /** Notify when a request is approved */
  requestApproved: boolean;
  /** Notify when a request is rejected */
  requestRejected: boolean;
  /** Notify when a comment is added */
  commentAdded: boolean;
  /** Notify when a request is submitted */
  requestSubmitted: boolean;
  /** Notify when a request is cancelled */
  requestCancelled: boolean;
}

/**
 * Default notification preferences (all enabled)
 */
const defaultPreferences: NotificationPreferences = {
  pendingApproval: true,
  requestApproved: true,
  requestRejected: true,
  commentAdded: true,
  requestSubmitted: true,
  requestCancelled: true,
};

/**
 * Default application icon for notifications
 */
const DEFAULT_ICON = '/icon-192.png';

/**
 * Default badge icon for notifications
 */
const DEFAULT_BADGE = '/badge-72.png';

/**
 * Check if the browser supports the Notification API
 */
function checkSupport(): boolean {
  return 'Notification' in window;
}

/**
 * Get current notification permission
 */
function getCurrentPermission(): NotificationPermission {
  if (!checkSupport()) {
    return 'denied';
  }
  return Notification.permission as NotificationPermission;
}

/**
 * Initial browser notifications state
 */
const initialBrowserNotificationsState: BrowserNotificationsState = {
  permission: getCurrentPermission(),
  enabled: true,
  isSupported: checkSupport(),
  preferences: { ...defaultPreferences },
};

/**
 * Browser notifications store with localStorage persistence for preferences
 */
const browserNotificationsStore: Store<BrowserNotificationsState> =
  createStore<BrowserNotificationsState>(initialBrowserNotificationsState, {
    persistKey: 'wyden-browser-notifications',
    serialize: (state) =>
      JSON.stringify({
        enabled: state.enabled,
        preferences: state.preferences,
      }),
    deserialize: (raw) => {
      const parsed = JSON.parse(raw);
      return {
        ...initialBrowserNotificationsState,
        permission: getCurrentPermission(),
        isSupported: checkSupport(),
        enabled: parsed.enabled ?? true,
        preferences: {
          ...defaultPreferences,
          ...parsed.preferences,
        },
      };
    },
  });

/**
 * Router instance for navigation on notification click
 */
let routerInstance: Router | null = null;

/**
 * Service worker registration for notification handling
 * Reserved for future implementation of persistent notifications via SW
 */
let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
void serviceWorkerRegistration; // Suppress unused variable warning

// ============================================================================
// Selectors
// ============================================================================

/**
 * Select current permission status
 */
export const selectPermission = (state: BrowserNotificationsState): NotificationPermission =>
  state.permission;

/**
 * Select whether notifications are enabled
 */
export const selectIsEnabled = (state: BrowserNotificationsState): boolean => state.enabled;

/**
 * Select whether browser supports notifications
 */
export const selectIsSupported = (state: BrowserNotificationsState): boolean => state.isSupported;

/**
 * Select notification preferences
 */
export const selectPreferences = (state: BrowserNotificationsState): NotificationPreferences =>
  state.preferences;

/**
 * Select whether notifications can be shown (supported, granted, enabled)
 */
export const selectCanShowNotifications = (state: BrowserNotificationsState): boolean =>
  state.isSupported && state.permission === 'granted' && state.enabled;

// ============================================================================
// Actions
// ============================================================================

/**
 * Request notification permission from the user
 * @returns Promise resolving to the granted permission
 */
export async function requestPermission(): Promise<NotificationPermission> {
  if (!checkSupport()) {
    browserNotificationsStore.setState({ permission: 'denied' });
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    browserNotificationsStore.setState({ permission: permission as NotificationPermission });
    return permission as NotificationPermission;
  } catch (error) {
    console.error('Failed to request notification permission:', error);
    browserNotificationsStore.setState({ permission: 'denied' });
    return 'denied';
  }
}

/**
 * Enable or disable browser notifications
 */
export function setNotificationsEnabled(enabled: boolean): void {
  browserNotificationsStore.setState({ enabled });
}

/**
 * Update notification preferences
 */
export function setNotificationPreferences(preferences: Partial<NotificationPreferences>): void {
  const currentPreferences = browserNotificationsStore.getState().preferences;
  browserNotificationsStore.setState({
    preferences: { ...currentPreferences, ...preferences },
  });
}

/**
 * Reset notification preferences to defaults
 */
export function resetNotificationPreferences(): void {
  browserNotificationsStore.setState({
    preferences: { ...defaultPreferences },
  });
}

/**
 * Set the router instance for navigation on notification click
 */
export function setBrowserNotificationRouter(router: Router): void {
  routerInstance = router;
}

/**
 * Set the service worker registration for persistent notifications
 */
export function setServiceWorkerRegistration(registration: ServiceWorkerRegistration): void {
  serviceWorkerRegistration = registration;
}

/**
 * Check if a specific notification type is enabled
 */
function isNotificationTypeEnabled(type: NotificationEventType): boolean {
  const state = browserNotificationsStore.getState();
  if (!state.enabled) return false;

  const preferences = state.preferences;
  switch (type) {
    case 'pending_approval':
      return preferences.pendingApproval;
    case 'request_approved':
      return preferences.requestApproved;
    case 'request_rejected':
      return preferences.requestRejected;
    case 'comment_added':
      return preferences.commentAdded;
    case 'request_submitted':
      return preferences.requestSubmitted;
    case 'request_cancelled':
      return preferences.requestCancelled;
    default:
      return true;
  }
}

/**
 * Show a browser notification
 * @returns The Notification instance or null if not shown
 */
export function showBrowserNotification(
  options: BrowserNotificationOptions
): Notification | null {
  const state = browserNotificationsStore.getState();

  // Check if notifications can be shown
  if (!state.isSupported || state.permission !== 'granted' || !state.enabled) {
    return null;
  }

  // Check if specific notification type is enabled
  if (options.data?.type && !isNotificationTypeEnabled(options.data.type)) {
    return null;
  }

  try {
    const notificationOptions: NotificationOptions = {
      body: options.body,
      icon: options.icon ?? DEFAULT_ICON,
      badge: options.badge ?? DEFAULT_BADGE,
      requireInteraction: options.requireInteraction ?? false,
    };

    // Add optional properties only if defined
    if (options.tag !== undefined) {
      notificationOptions.tag = options.tag;
    }
    if (options.data !== undefined) {
      notificationOptions.data = options.data;
    }

    // Add vibrate if supported (mobile)
    if (options.vibrate) {
      (notificationOptions as Record<string, unknown>)['vibrate'] = options.vibrate;
    }

    const notification = new Notification(options.title, notificationOptions);

    // Handle notification click
    notification.onclick = (event) => {
      event.preventDefault();
      handleNotificationClick(notification);
    };

    // Handle notification close
    notification.onclose = () => {
      // Cleanup if needed
    };

    // Handle notification error
    notification.onerror = (error) => {
      console.error('Notification error:', error);
    };

    // Auto-close notification after timeout
    if (options.autoClose && options.autoClose > 0) {
      setTimeout(() => {
        notification.close();
      }, options.autoClose);
    }

    return notification;
  } catch (error) {
    console.error('Failed to show notification:', error);
    return null;
  }
}

/**
 * Handle notification click - navigate to relevant page
 */
function handleNotificationClick(notification: Notification): void {
  // Focus the window
  window.focus();

  // Close the notification
  notification.close();

  const data = notification.data as BrowserNotificationData | undefined;

  if (!data) return;

  // Navigate to the URL if provided
  if (data.url && routerInstance) {
    routerInstance.navigate(data.url);
  } else if (data.requestId && routerInstance) {
    // Navigate to the request detail page
    routerInstance.navigate(`/requests/${data.requestId}`);
  }
}

// ============================================================================
// Convenience notification creators for specific events
// ============================================================================

/**
 * Show notification for a pending approval
 */
export function notifyPendingApproval(
  requestId: string,
  requestTitle: string,
  requesterName: string
): Notification | null {
  return showBrowserNotification({
    title: 'New Approval Request',
    body: `${requesterName} submitted "${requestTitle}" for your approval`,
    tag: `pending-approval-${requestId}`,
    requireInteraction: true,
    data: {
      type: 'pending_approval',
      requestId,
      requestTitle,
      url: `/requests/${requestId}`,
    },
  });
}

/**
 * Show notification when a request is approved
 */
export function notifyRequestApproved(
  requestId: string,
  requestTitle: string,
  approverName: string,
  isFullyApproved: boolean
): Notification | null {
  const title = isFullyApproved ? 'Request Approved' : 'Request Partially Approved';
  const body = isFullyApproved
    ? `Your request "${requestTitle}" has been fully approved`
    : `${approverName} approved your request "${requestTitle}"`;

  return showBrowserNotification({
    title,
    body,
    tag: `approved-${requestId}`,
    data: {
      type: 'request_approved',
      requestId,
      requestTitle,
      url: `/requests/${requestId}`,
      metadata: { isFullyApproved, approverName },
    },
  });
}

/**
 * Show notification when a request is rejected
 */
export function notifyRequestRejected(
  requestId: string,
  requestTitle: string,
  rejectorName: string,
  reason?: string
): Notification | null {
  const body = reason
    ? `${rejectorName} rejected "${requestTitle}": ${reason}`
    : `${rejectorName} rejected your request "${requestTitle}"`;

  return showBrowserNotification({
    title: 'Request Rejected',
    body,
    tag: `rejected-${requestId}`,
    requireInteraction: true,
    data: {
      type: 'request_rejected',
      requestId,
      requestTitle,
      url: `/requests/${requestId}`,
      metadata: { rejectorName, reason },
    },
  });
}

/**
 * Show notification when a comment is added to a request
 */
export function notifyCommentAdded(
  requestId: string,
  requestTitle: string,
  commenterName: string,
  commentPreview: string
): Notification | null {
  const truncatedComment =
    commentPreview.length > 100 ? `${commentPreview.substring(0, 97)}...` : commentPreview;

  return showBrowserNotification({
    title: `New Comment on "${requestTitle}"`,
    body: `${commenterName}: ${truncatedComment}`,
    tag: `comment-${requestId}-${Date.now()}`,
    data: {
      type: 'comment_added',
      requestId,
      requestTitle,
      url: `/requests/${requestId}`,
      metadata: { commenterName },
    },
  });
}

/**
 * Show notification when a request is submitted (for admin/assistant views)
 */
export function notifyRequestSubmitted(
  requestId: string,
  requestTitle: string,
  requesterName: string,
  amount: number,
  currency: string
): Notification | null {
  return showBrowserNotification({
    title: 'New Expenditure Request',
    body: `${requesterName} submitted "${requestTitle}" for ${currency} ${amount.toLocaleString()}`,
    tag: `submitted-${requestId}`,
    data: {
      type: 'request_submitted',
      requestId,
      requestTitle,
      url: `/requests/${requestId}`,
      metadata: { requesterName, amount, currency },
    },
  });
}

/**
 * Show notification when a request is cancelled
 */
export function notifyRequestCancelled(
  requestId: string,
  requestTitle: string,
  cancellerName: string
): Notification | null {
  return showBrowserNotification({
    title: 'Request Cancelled',
    body: `${cancellerName} cancelled the request "${requestTitle}"`,
    tag: `cancelled-${requestId}`,
    data: {
      type: 'request_cancelled',
      requestId,
      requestTitle,
      url: `/requests/${requestId}`,
    },
  });
}

// ============================================================================
// Permission UI helpers
// ============================================================================

/**
 * Check if we should prompt the user for permission
 * Returns true if permission is 'default' and notifications are supported
 */
export function shouldPromptForPermission(): boolean {
  const state = browserNotificationsStore.getState();
  return state.isSupported && state.permission === 'default';
}

/**
 * Check if permission was denied (user explicitly said no)
 */
export function isPermissionDenied(): boolean {
  const state = browserNotificationsStore.getState();
  return state.permission === 'denied';
}

/**
 * Check if permission was granted
 */
export function isPermissionGranted(): boolean {
  const state = browserNotificationsStore.getState();
  return state.permission === 'granted';
}

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize browser notifications service
 * Should be called on app startup
 */
export function initBrowserNotifications(): void {
  // Update permission status (may have changed externally)
  browserNotificationsStore.setState({
    permission: getCurrentPermission(),
    isSupported: checkSupport(),
  });

  // Listen for visibility change to update permission
  // (user might change in browser settings while tab is hidden)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      browserNotificationsStore.setState({
        permission: getCurrentPermission(),
      });
    }
  });
}

// ============================================================================
// Store exports
// ============================================================================

/**
 * Get current browser notifications state (readonly)
 */
export function getBrowserNotificationsState(): Readonly<BrowserNotificationsState> {
  return browserNotificationsStore.getState();
}

/**
 * Subscribe to browser notifications state changes
 */
export const subscribeToBrowserNotifications = browserNotificationsStore.subscribe.bind(
  browserNotificationsStore
);

/**
 * Subscribe to selected browser notifications state
 */
export const subscribeToBrowserNotificationsSelector =
  browserNotificationsStore.subscribeToSelector.bind(browserNotificationsStore);

export { browserNotificationsStore };
